import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The sign-up form. The auth library sends a verification link and creates no
// session; the entry point lands the browser on check-your-inbox.
// See ADR-0042.

type SignUpFormProps = {
  // Set when the person is signing up to accept this invitation.
  invitationId?: string | null;
  onSignedUp: (email: string) => void;
};

/**
 * Creates an account from email, password and an Organisation name, and
 * calls `onSignedUp` once the verification mail is requested. With an
 * `invitationId` the Organisation-name field is dropped and the invitation
 * rides along, so verification founds nothing. Rendered by the public entry
 * at `/sign-up`.
 */
export function SignUpForm({ invitationId = null, onSignedUp }: SignUpFormProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const invitee = invitationId !== null;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authPost("/sign-up/email", {
        email,
        password,
        ...(invitee
          ? {
              pendingInvitationId: invitationId,
              callbackURL: `/accept-invitation?id=${encodeURIComponent(invitationId)}`,
            }
          : { pendingOrganisationName: organisationName, callbackURL: "/" }),
      });
      onSignedUp(email);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-card">
      <form onSubmit={onSubmit} aria-label="Sign up">
        <h1>{invitee ? "Create your account" : "Create your Organisation"}</h1>
        <p className="lede">
          {invitee
            ? "You have been invited. Set a password and we will send you a link to confirm your address; then you join the Organisation."
            : "One form, then a link in your inbox. Clicking it creates your Organisation and signs you in as its owner."}
        </p>
        <div className="field">
          <label htmlFor="signup-email">Email</label>
          <input
            id="signup-email"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="signup-password">Password</label>
          <input
            id="signup-password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {invitee ? null : (
          <div className="field">
            <label htmlFor="signup-organisation">Organisation name</label>
            <input
              id="signup-organisation"
              type="text"
              name="organisationName"
              required
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
            />
          </div>
        )}
        {error ? (
          <div className="notice bad" role="alert">
            <div>{error}</div>
          </div>
        ) : null}
        <div className="btn-row">
          <button type="submit" className="btn primary lg block" disabled={submitting}>
            {submitting ? "Signing up…" : invitee ? "Create account" : "Create Organisation"}
          </button>
        </div>
        <p className="hint">
          Already have an account? <a href="/login">Sign in</a>.
        </p>
      </form>
    </div>
  );
}
